import { FC } from 'react'

import { FormProvider, useForm } from 'react-hook-form'

import { LoadingContainer } from 'peach/components'
import { StyledButton } from 'peach/components/Button/Button'
import Input from 'peach/components/Input'
import SelectInput from 'peach/components/SelectInput'

import { trueFalseSelectOptions } from 'core/constants'
import { R } from 'core/helpers'
import { useInvestorCreate } from 'core/types'

import { ErrorComponent, FormActions, Title } from './Components'
import { Company } from './types'

type CreateInvestorFormValues = {
  legalName?: string
  default?: boolean
}

type CreateInvestorFormProps = {
  company: Company
  updateCompany: (obj: Company) => void
  title: string
}

const CreateInvestorForm: FC<CreateInvestorFormProps> = ({
  company,
  updateCompany,
  title,
}) => {
  const form = useForm<CreateInvestorFormValues>({
    values: {
      legalName:
        company?.legalName ?? company?.name ?? company?.brandName ?? '',
      default: true,
    },
  })

  const {
    mutateAsync: createInvestor,
    isPending,
    isError,
    error,
  } = useInvestorCreate({
    companyId: company?.companyId,
  })

  const onCreate = async (values) => {
    const resp = await createInvestor(values)

    if (resp?.status === 201 && !R.isEmpty(resp?.data)) {
      updateCompany({
        ...company,
        investors: [
          ...(company?.investors ?? []),
          {
            id: resp?.data?.id,
            legalName: resp?.data?.legalName,
            default: resp?.data?.default,
          },
        ],
      })
    }

    return resp
  }

  return (
    <LoadingContainer loading={isPending}>
      <Title>{title}</Title>
      <form
        onSubmit={form.handleSubmit(async (values) => {
          try {
            await onCreate(values)
          } catch (e) {
            console.error('Error with create actions: ', e)
          }
        })}
      >
        <FormProvider {...form}>
          <Input
            label='Legal Name'
            required
            type='text'
            {...form.register('legalName')}
          />

          <SelectInput
            control={form.control}
            label='Set Lender As Default Investor'
            name='default'
            options={trueFalseSelectOptions}
            placeholder='Select true/false'
          />

          {isError &&
            !!error &&
            typeof error === 'object' &&
            'message' in error &&
            typeof error.message === 'string' && (
              <ErrorComponent>{error.message}</ErrorComponent>
            )}
          <FormActions>
            <StyledButton $primary type='submit'>
              Submit
            </StyledButton>
          </FormActions>
        </FormProvider>
      </form>
    </LoadingContainer>
  )
}

export default CreateInvestorForm
